import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Form from "../../components/base/Form/Form";
import WilmaLogo from "../../assets/logo.svg";
import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { FormErrors } from "../../components/base/Form/FormErrors";
import { FormTextField } from "../../components/base/Form/FormTextField";
import useApiHelper from "../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../models/system/DefaultResponse";
import { shopifyRedirectUrl } from "../../config";
// import { useAuth } from "../../contexts/ThemeContext"; // <-- change if your AuthContext path is different

interface ShopifyLoginRequest {
  shop: string;
  clientId: string;
  username: string;
  password: string;
}

interface ShopifyLoginResponse {
  customerId: string;
  userId: string;
  domain: string;
  clientId: string;
}

interface LoginFormFields {
  username: string;
  password: string;
}

export default function ShopifyInstallLogin() {
  const [urlParams] = useSearchParams();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { post } = useApiHelper(); // used in TODO section once you wire up the endpoint
  // const { customerId, userId } = useAuth(); // assumes this provides these after login



  const [shop, setShop] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string>();

  const startShopifyInstall = useCallback(
    (shopDomain: string, customerId: string, userId: string, wilmaDomain: string, appClientId: string) => {

      if (!customerId || !userId) {
        setAuthError(
          "Invalid request: Error code X01283"
        );
        return;
      }

      if (!appClientId) {
        setAuthError(
          "Invalid request: Error code X58463"
        );
        return;
      }

      const scopes =
        "write_products,write_orders,write_fulfillments,write_customers,write_locations,write_publications,write_inventory";
      
        // Maybe redirect them to the actual site
      const state = `${customerId}|${userId}|${document.location.origin}/shopify/verify?domain=${wilmaDomain}|${appClientId}`;

      const url =
        `https://${shopDomain}/admin/oauth/authorize` +
        `?client_id=${appClientId}` +
        `&scope=${encodeURIComponent(scopes)}` +
        `&redirect_uri=${encodeURIComponent(shopifyRedirectUrl ?? "")}` +
        `&state=${encodeURIComponent(state)}`;
      // no `&grant_options[]=per-user`... makes it expire after 24 hours otherwise

      console.log("Opening Shopify auth URL:", url);
      window.open(url, "_blank");
    },
    [shopifyRedirectUrl]
  );

  useEffect(() => {
    const shopParam = urlParams.get("shop");
    const clientIdParam = urlParams.get("client_id") ?? urlParams.get("api_key");

    if (!shopParam) {
      setAuthError("Invalid request: Error code X37600");
      setLoading(false);
      return;
    }
    if (!clientIdParam) {
      setAuthError("Invalid request: missing Shopify app id (client_id)");
      setLoading(false);
      return;
    }

    setShop(shopParam);
    setClientId(clientIdParam);

    setLoading(false);
  }, [urlParams, startShopifyInstall]);

  const handleLogin = async (data: LoginFormFields) => {
    setAuthError(undefined);
    setLoading(true);

    try {
      if (!shop || !clientId) {
        setAuthError("Invalid request: Error code X37600");
        return;
      }

      const loginResponse = await post<ShopifyLoginRequest, DefaultResponseWithData<ShopifyLoginResponse>>(
        "/integration/shopify/login",
        { ...data, shop, clientId }
      );
      if (!loginResponse.success) {
        setAuthError(loginResponse.message);
        return;
      }

      // Once logged in and AuthContext is populated, kick off the Shopify OAuth install flow:
      startShopifyInstall(
        shop,
        loginResponse.data.customerId,
        loginResponse.data.userId,
        loginResponse.data.domain,
        loginResponse.data.clientId
      );
    } catch (e: any) {
      if (e?.status) {
        setAuthError(e.message);
      } else {
        setAuthError("Unexpected error, please contact support");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:pt-16 sm:px-6 lg:px-8">
      <div className="mb-12 flex justify-center sm:mb-16 sm:mx-auto sm:w-full sm:max-w-md">
        <img className="h-14" src={WilmaLogo} alt="Logo" />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-12 shadow prose sm:rounded-lg sm:px-12">
          <h2>Connect your Wilma account</h2>
          {shop && (
            <p className="text-sm text-gray-600">
              To install the Wilma Shopify app for{" "}
              <span className="font-semibold">{shop}</span>, please sign in to
              your Wilma account.
            </p>
          )}

          <Form<LoginFormFields> onSubmit={handleLogin} className="mt-6 space-y-4">
            {({ register }) =>
              loading ? (
                <LoadingWheel />
              ) : (
                <>
                  {authError && (
                    <Alert
                      severity="error"
                      title="Login Error"
                      message={authError}
                    />
                  )}
                  <FormErrors />
                  <FormTextField
                    label="Username"
                    {...register("username", { required: true })}
                  />
                  <FormTextField
                    label="Password"
                    type="password"
                    {...register("password", { required: true })}
                  />

                  <Button type="submit" className="!mt-10" fullWidth>
                    Login
                  </Button>
                </>
              )
            }
          </Form>
        </div>
      </div>
    </div>
  );
}
